import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Validates if a string is a valid UUID
 * @param str String to validate
 * @returns true if the string is a valid UUID
 */
export function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

// Utility function for consistent sidebar menu item styling
export function getSidebarItemClass(isActive: boolean): string {
  return isActive
    ? "bg-[#009ce5]/20 text-[#009ce5] font-medium w-full text-left px-3 py-2 rounded-md text-sm transition-colors"
    : "hover:bg-[#009ce5]/10 text-gray-700 w-full text-left px-3 py-2 rounded-md text-sm transition-colors";
}

// Utility function to format date to MM-DD-YYYY from datepicker for display
export function formatDateMMDDYYYY(dateString?: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString + 'T00:00:00');
      if (isNaN(date.getTime())) return '';
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    } catch {
      console.error(`Error formatting date: ${dateString}`);
      return '';
    }
  }

/**
 * Converts UTC date string to local date for display purposes
 * @param utcDateString - UTC date string (ISO format or YYYY-MM-DD)
 * @param format - Output format ('MM/DD/YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD')
 * @returns Formatted local date string
 */
export function formatUTCToLocalDate(utcDateString?: string, format: 'MM/DD/YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD' = 'MM/DD/YYYY'): string {
  if (!utcDateString) return '';
  
  try {
    let date: Date;
    let cleanDateString = utcDateString;
    
    // Fix malformed date format from API: "2025-10-30T00:00:00:00:000" -> "2025-10-30T00:00:00"
    // Treat as local time to avoid timezone shifting for date-only values
    if (utcDateString.match(/T\d{2}:\d{2}:\d{2}:\d{2}:\d{3}$/)) {
      cleanDateString = utcDateString.replace(/T(\d{2}):(\d{2}):(\d{2}):(\d{2}):(\d{3})$/, 'T$1:$2:$3');
    }
    
    if (cleanDateString.includes('T')) {
      // Full ISO string with time - but check if it's just midnight (date-only value)
      if (cleanDateString.match(/T00:00:00(\.000)?([+-]\d{2}:\d{2}|Z)?$/)) {
        // This is a date-only value with midnight time, treat as local to avoid timezone shift
        const dateOnly = cleanDateString.split('T')[0];
        date = new Date(dateOnly + 'T00:00:00');
      } else {
        // This is a real timestamp, parse as-is
        date = new Date(cleanDateString);
      }
    } else {
      // Date only string (YYYY-MM-DD) to avoid timezone shifting
      date = new Date(cleanDateString + 'T00:00:00');
    }
    
    if (isNaN(date.getTime())) {
      console.warn(`Invalid UTC date string: ${utcDateString} (cleaned: ${cleanDateString})`);
      return '';
    }
    
    // Get local date components (no timezone conversion needed for date-only strings)
    const localYear = date.getFullYear();
    const localMonth = String(date.getMonth() + 1).padStart(2, '0');
    const localDay = String(date.getDate()).padStart(2, '0');
    
    // Return in requested format
    switch (format) {
      case 'MM/DD/YYYY':
        return `${localMonth}/${localDay}/${localYear}`;
      case 'MM-DD-YYYY':
        return `${localMonth}-${localDay}-${localYear}`;
      case 'YYYY-MM-DD':
        return `${localYear}-${localMonth}-${localDay}`;
      default:
        return `${localMonth}/${localDay}/${localYear}`;
    }
  } catch (error) {
    console.error(`Error formatting UTC date: ${utcDateString}`, error);
    return '';
  }
}
/**
 * Converts UTC date string to input-friendly YYYY-MM-DD format for date pickers
 * @param utcDateString - UTC date string
 * @returns YYYY-MM-DD format for HTML date inputs
 */
export function formatUTCToInputDate(utcDateString?: string): string {
  if (!utcDateString) return '';
  
  try {
    let date: Date;
    let cleanDateString = utcDateString;
    
    // Fix malformed date format from API: "2025-10-30T00:00:00:00:000" -> "2025-10-30T00:00:00"
    if (utcDateString.match(/T\d{2}:\d{2}:\d{2}:\d{2}:\d{3}$/)) {
      cleanDateString = utcDateString.replace(/T(\d{2}):(\d{2}):(\d{2}):(\d{2}):(\d{3})$/, 'T$1:$2:$3');
    }
    
    if (cleanDateString.includes('T')) {
      // Full ISO string with time - but check if it's just midnight (date-only value)
      if (cleanDateString.match(/T00:00:00(\.000)?([+-]\d{2}:\d{2}|Z)?$/)) {
        // This is a date-only value with midnight time, treat as local to avoid timezone shift
        const dateOnly = cleanDateString.split('T')[0];
        date = new Date(dateOnly + 'T00:00:00');
      } else {
        // This is a real timestamp, parse as-is
        date = new Date(cleanDateString);
      }
    } else {
      // Date only string - treat as local to avoid timezone shifting
      date = new Date(cleanDateString + 'T00:00:00');
    }
    
    if (isNaN(date.getTime())) {
      console.warn(`Invalid UTC date string: ${utcDateString} (cleaned: ${cleanDateString})`);
      return '';
    }
    
    // Return in YYYY-MM-DD format for date inputs
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error(`Error formatting UTC date to input format: ${utcDateString}`, error);
    return '';
  }
}
/**
 * Converts local date input (YYYY-MM-DD) to UTC ISO string for saving
 * @param localDateString - Local date string in YYYY-MM-DD format
 * @returns UTC ISO string
 */
export function formatLocalToUTCDate(localDateString: string): string {
  if (!localDateString) return '';
  
  try {
    // Create date object from local date string
    const date = new Date(localDateString + 'T00:00:00');
    
    if (isNaN(date.getTime())) {
      console.warn(`Invalid local date string: ${localDateString}`);
      return '';
    }
    
    // Return as UTC ISO string
    return date.toISOString();
  } catch (error) {
    console.error(`Error converting local date to UTC: ${localDateString}`, error);
    return '';
  }
}
/**

/**
 * Helper function to generate consistent input styling classes across forms
 * @param isEditing Whether the form is in edit mode
 * @param isReadOnly Whether the field should appear as read-only
 * @param isPrimary Whether the field should have primary field styling
 * @returns Tailwind CSS classes string
 */
export function getInputClassName(
  isEditing: boolean,
  isReadOnly = false,
  isPrimary = false
): string {
  let classes = "";
  
  if (isReadOnly) {
    classes += "bg-gray-100 cursor-text ";
  }
    if (!isEditing) {
    classes += "opacity-100 cursor-not-allowed bg-transparent border-0 shadow-none p-0 focus:ring-0 focus-visible:ring-0 ";
  } else {    // Enhanced styling for editable inputs - standardized highlight color to match insured name style
    classes += "rounded-md transition-all duration-200 focus:border-[#009ce5] focus:ring-[#009ce5]/30 focus-visible:border-[#009ce5] focus-visible:ring-[#009ce5]/30 ";
  }
    if (isPrimary) {
    // Removed different border color for primary/required fields
    classes += "font-medium ";
  }

  return classes.trim();
}

/**
 * Converts a Date object to YYYY-MM-DD format without timezone issues
 * This is specifically for date picker onChange handlers
 * @param date - Date object from date picker
 * @returns YYYY-MM-DD format string
 */
export function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}